/**
 * Google Tag (gtag.js) installer + dataLayer event helper.
 *
 * The brand-owned Google Tag workspace at tagmanager.google.com handles
 * all routing (to GA4, Meta Pixel, TikTok Pixel, etc.). This file's
 * only job is to:
 *
 *   1. Inject `gtag.js` into the page after the user accepts cookies.
 *   2. Expose a single `pushEvent(name, props)` that fans into
 *      `window.dataLayer`, which the Google Tag workspace then routes
 *      to whichever destinations marketing has configured.
 *
 * Inert when `NEXT_PUBLIC_GOOGLE_TAG_ID` is unset (local dev that
 * doesn't want telemetry, branch previews before the env is wired,
 * etc.). Idempotent — calling `initGoogleTag` repeatedly is a no-op.
 *
 * Mirrors the shape of `posthog.ts` for consistency.
 */

const TAG_SCRIPT_ID = 'jas-google-tag-loader';

let initialised = false;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown> | IArguments>;
    // gtag itself is just `function gtag(){dataLayer.push(arguments);}`.
    // Loose typing — the Google Tag's `arguments` object is variadic.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Initialise the Google Tag. Safe to call on the server (no-op) and
 * safe to call repeatedly (idempotent). Returns true when the tag
 * actually loaded (or was already loaded), false when skipped.
 */
export function initGoogleTag(): boolean {
  if (initialised) return true;
  if (typeof window === 'undefined') return false;

  const id = process.env.NEXT_PUBLIC_GOOGLE_TAG_ID;
  if (!id) return false;

  // dataLayer + gtag stub must exist before the loader script runs,
  // so any early `gtag()` calls between init and script-load queue up
  // into dataLayer and replay once gtag.js boots.
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  window.gtag('js', new Date());
  window.gtag('config', id);

  // Inject the loader script. Idempotent — if a previous mount already
  // appended it (e.g. consent toggled twice), don't append again.
  if (!document.getElementById(TAG_SCRIPT_ID)) {
    const s = document.createElement('script');
    s.id = TAG_SCRIPT_ID;
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
  }

  initialised = true;
  return true;
}

/**
 * Send a custom event to GA4 via the Google Tag.
 *
 * gtag.js processes ONLY arguments-shaped dataLayer pushes — i.e. what
 * `gtag('event', name, params)` produces under the hood. The
 * object-with-`event`-key push (`{event: name, ...}`) is Google Tag
 * Manager *Container* syntax — gtag.js silently ignores it.
 *
 * The original implementation in #67 pushed objects and was caught in
 * #68's verification walk: only the auto `page_view` (fired by
 * `gtag('config')`) reached GA4; every `track()` call from the app
 * code disappeared.
 *
 * Defensive stub init: if gtag isn't on `window` yet (analytics
 * consent not yet granted, or this function called before
 * `initGoogleTag`), we install the same stub `initGoogleTag` uses.
 * The push then queues into dataLayer, and gtag.js replays it on
 * boot — or it stays queued forever if gtag.js never loads, which is
 * the correct no-op behaviour.
 */
export function pushGoogleTagEvent(
  name: string,
  props: Record<string, string | number | boolean | null | undefined> = {},
): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  window.gtag('event', name, props);
}

export function isGoogleTagReady(): boolean {
  return initialised;
}
